import { IonIcon } from "@ionic/react";
import { playCircle } from "ionicons/icons";
import { ReactElement, useState } from "react";
import styled from "styled-components";
import { useHlsPlayer } from "../../../../hooks/useHlsPlayer";

interface VideoData {
  uid?: string;
  caption?: string;
}

// Cloudflare Stream URL scheme — same construction as VideoOnboarding.tsx
const manifestUrl = (uid: string) => `https://videodelivery.net/${uid}/manifest/video.m3u8`;
const thumbnailUrl = (uid: string) => `https://videodelivery.net/${uid}/thumbnails/thumbnail.jpg`;

const Wrap = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: #000;
  aspect-ratio: 16 / 9;
`;

const Thumb = styled.button`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  cursor: pointer;
  background: transparent;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const PlayBadge = styled(IonIcon)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 64px;
  color: rgba(255, 255, 255, 0.92);
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.45));
  pointer-events: none;
`;

const Player = styled.video`
  width: 100%;
  height: 100%;
  display: block;
`;

const Caption = styled.p`
  font-size: 13px;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.6);
  margin: 6px 0 0;
  text-align: center;
`;

// Thumbnail-first, tap to play (never autoplay — this sits inside article-like pages).
// Mounting the HLS pipeline only after the tap keeps pages with several videos light.
const VideoPlayer = ({ uid }: { uid: string }): ReactElement => {
  const { videoRef } = useHlsPlayer({
    src: manifestUrl(uid),
    active: true,
    muted: false,
    shouldPlay: true,
  });
  return <Player ref={videoRef} controls playsInline poster={thumbnailUrl(uid)} />;
};

const VideoBlock = ({ data }: { data: VideoData }): ReactElement | null => {
  const [playing, setPlaying] = useState(false);
  if (!data.uid) return null;
  return (
    <div>
      <Wrap>
        {playing ? (
          <VideoPlayer uid={data.uid} />
        ) : (
          <Thumb onClick={() => setPlaying(true)} aria-label={data.caption || "Play video"}>
            <img src={thumbnailUrl(data.uid)} alt="" loading="lazy" />
            <PlayBadge icon={playCircle} />
          </Thumb>
        )}
      </Wrap>
      {data.caption ? <Caption>{data.caption}</Caption> : null}
    </div>
  );
};

export default VideoBlock;
